import type { AnswerInput, AnswerResult } from '@qq/shared';
import { ApiRequestError, api } from './api.js';

/**
 * Never lose an answer.
 *
 * An iPad on family Wi-Fi drops out. When it does, the child must not lose
 * their work and must not see a crash. Every answer is POSTed with a
 * client-generated attempt id, so:
 *
 *   - a brief drop is handled by retrying for a few seconds; the child sees a
 *     moment's pause and then their feedback,
 *   - a longer outage stores the attempt in IndexedDB and lets the child carry
 *     on. It flushes on reconnect, and the server upserts on the attempt id, so
 *     a replay can never count an answer twice.
 *
 * The honest cost of the offline path: with no server, nothing can tell the
 * child whether they were right, because the answer key is deliberately not in
 * the browser. So they get a neutral "saved, keep going" rather than a guess.
 */

const DB_NAME = 'quokka-attempts';
const STORE = 'pending';
const RETRY_MS = [400, 1200, 2500, 4000];

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE))
        db.createObjectStore(STORE, { keyPath: 'attemptId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function pendingCount(): Promise<number> {
  return (await withStore('readonly', (s) => s.count())) ?? 0;
}

/** Sends an answer, retrying briefly, then queuing rather than losing it. */
export async function submitAnswer(input: AnswerInput): Promise<AnswerResult | 'queued'> {
  for (let attempt = 0; attempt <= RETRY_MS.length; attempt++) {
    try {
      return await api<AnswerResult>('/child/activities/answer', { method: 'POST', body: input });
    } catch (err) {
      // A refusal from the server is an answer, not a network problem: retrying
      // it would just fail again, and queuing it would fail forever.
      if (err instanceof ApiRequestError) throw err;
      const wait = RETRY_MS[attempt];
      if (wait === undefined) break;
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  await withStore('readwrite', (s) => s.put({ ...input, queuedAt: Date.now() }));
  return 'queued';
}

/** Flushes what is waiting. Safe to call at any time and as often as you like. */
export async function flushQueue(): Promise<{ sent: number; remaining: number }> {
  const items = (await withStore<AnswerInput[]>('readonly', (s) => s.getAll())) ?? [];
  let sent = 0;

  for (const item of items) {
    try {
      await api<AnswerResult>('/child/activities/answer', { method: 'POST', body: item });
      await withStore('readwrite', (s) => s.delete(item.attemptId));
      sent += 1;
    } catch (err) {
      if (err instanceof ApiRequestError && err.status >= 400 && err.status < 500) {
        // The server will never accept this one - an expired question token,
        // most likely. Drop it rather than retrying forever, and say so.
        console.warn('dropping an attempt the server rejected', err.body.code);
        await withStore('readwrite', (s) => s.delete(item.attemptId));
      } else {
        break; // still offline; try again next time
      }
    }
  }

  return { sent, remaining: await pendingCount() };
}

/** Flush on reconnect and at start-up. */
export function watchConnection(): () => void {
  const onOnline = () => void flushQueue();
  window.addEventListener('online', onOnline);
  void flushQueue();
  return () => window.removeEventListener('online', onOnline);
}
